// v2/pages/DebtV2.js — Borc Yaslandirma (FIFO, eski sistemle ayni hesap)
// URL: ?tab=overview|moves &q= &from= &to= &type=debit|credit &page= &per=
import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  Wallet, AlertTriangle, CheckCircle2, Clock, Search, X, Calendar, Filter,
  ChevronLeft, ChevronRight, CreditCard, ExternalLink, RotateCw, Inbox, ShieldAlert,
  ArrowDownLeft, ArrowUpRight, History,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ToastStack from '../components/Toast';
import useToast from '../hooks/useToast';
import useMenuNav from '../hooks/useMenuNav';
import useCartContext from '../hooks/CartContext';
import useExchangeRates from '../hooks/useExchangeRates';
import { useDebtSummary, useDebtTransactions } from '../hooks/useDebt';
import { formatTRY, formatNumber, formatPercent, formatCompact } from '../utils/format';
import { tarihTR } from '../utils/mapAccount';
import styles from '../styles/DashboardV2.module.css';
import o from '../styles/OrdersV2.module.css';
import d from '../styles/DebtV2.module.css';
import '../styles/tokens.css';

const RISK = {
  clean:    { label: 'Borcunuz yok', cls: 'rkClean' },
  low:      { label: 'Güncel', cls: 'rkLow' },
  medium:   { label: 'Dikkat', cls: 'rkMed' },
  high:     { label: 'Uyarı', cls: 'rkHigh' },
  critical: { label: 'Kritik', cls: 'rkCrit' },
  severe:   { label: 'Ciddi', cls: 'rkSevere' },
  very_severe: { label: 'Çok ciddi', cls: 'rkVSevere' },
  extreme:  { label: 'Çok kritik', cls: 'rkExtreme' },
};
const TONE_RENK = {
  current: '#16A36A', watch: '#CA8A04', warn: '#EA580C',
  danger: '#DC2626', severe: '#B91C1C', critical: '#7F1D1D', extreme: '#4C0519',
  future: '#94A3B8',
};
const TONE_ACIKLAMA = {
  current: 'Güncel', watch: 'Dikkat', warn: 'Uyarı', danger: 'Riskli', severe: 'Ciddi',
  critical: 'Kritik', extreme: 'Çok kritik',
};
const DILIM_TONU = {
  '0-30': 'current', '31-60': 'watch', '61-90': 'warn', '91-120': 'danger', '121-150': 'severe',
  '151-180': 'critical', '180+': 'extreme',
};
const BELGE_ONIZLEME = 10;
const PER_PAGE = [15, 30, 50];
const TIPLER = [
  { id: '', label: 'Tüm Hareketler' },
  { id: 'debit', label: 'Borç (fatura vb.)' },
  { id: 'credit', label: 'Alacak (ödeme vb.)' },
];
const TARIH_RX = /^\d{4}-\d{2}-\d{2}$/;

// ---------- URL <-> filtre ----------
const BOS = { tab: 'overview', q: '', from: '', to: '', type: '', page: 1, per: PER_PAGE[0] };
const URL_ALAN = ['tab', 'q', 'from', 'to', 'type', 'page', 'per'];

function duzelt(f) {
  const n = { ...BOS, ...f };
  if (n.tab !== 'moves') n.tab = 'overview';
  n.q = String(n.q || '').slice(0, 60);
  if (TARIH_RX.test(n.from) === false) n.from = '';
  if (TARIH_RX.test(n.to) === false) n.to = '';
  if (TIPLER.some((t) => t.id === n.type) === false) n.type = '';
  n.page = Math.max(parseInt(n.page, 10) || 1, 1);
  n.per = PER_PAGE.includes(Number(n.per)) ? Number(n.per) : BOS.per;
  return n;
}

function urldenOku(sp) {
  const f = {};
  URL_ALAN.forEach((k) => {
    const v = sp.get(k);
    if (v !== null) f[k] = v;
  });
  return duzelt(f);
}

function urleYaz(f) {
  const p = new URLSearchParams();
  URL_ALAN.forEach((k) => {
    const v = f[k];
    if (v === '' || v === null || v === undefined || v === BOS[k]) return;
    if (f.tab === 'overview' && k !== 'tab') return;
    p.set(k, String(v));
  });
  return p.toString();
}

// ---------- parcalar ----------
function OdemeLinki({ url, className, children }) {
  if (url === '') return null;
  return (
    <a className={className} href={url} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function KisitlamaBandi({ r, url }) {
  if (r === null || r.active === false) return null;
  return (
    <div className={`${d.banner} ${r.blocked ? d.bannerRed : d.bannerAmber}`} role="alert">
      <ShieldAlert size={24} />
      <div className={d.bannerBody}>
        <span className={d.bannerTitle}>
          {r.blocked ? 'Ödeme süreniz doldu' : 'Vadesi geçmiş borcunuz bulunuyor'}
        </span>
        {r.message && <span className={d.bannerText}>{r.message}</span>}
        <span className={d.bannerMeta}>
          Gereken ödeme: <b>{formatTRY(r.requiredPayment)}</b>
          {r.overdueDays !== null && <> ({formatNumber(r.overdueDays)} günden eski borçlar)</>}
          {r.dueDate && <> · Son ödeme tarihi: <b>{tarihTR(r.dueDate)}</b></>}
          {r.blocked === false && r.daysLeft !== null && <> · <b>{formatNumber(r.daysLeft)} gün</b> kaldı</>}
        </span>
      </div>
      <OdemeLinki url={url} className={d.payBtn}>
        <CreditCard size={16} /> Ödeme Yap <ExternalLink size={13} />
      </OdemeLinki>
    </div>
  );
}

function StatCards({ s }) {
  const net = s.totals.net;
  const durum = net > 0.005 ? 'borçlu' : (net < -0.005 ? 'alacaklı' : 'dengede');
  const eskiGun = s.oldestDays !== null && s.oldestDays >= 0 ? formatNumber(s.oldestDays) : '—';
  const items = [
    { key: 'n', Icon: Wallet, cls: o.iconBlue, label: 'Net Bakiye',
      value: formatTRY(Math.abs(net)), unit: durum },
    { key: 'o', Icon: AlertTriangle, cls: d.iconRed, label: 'Vadesi Geçmiş (30+ gün)',
      value: formatTRY(s.overdue) },
    { key: 'c', Icon: CheckCircle2, cls: o.iconGreen, label: 'Güncel Borç (0-30 gün)',
      value: formatTRY(s.current) },
    { key: 'e', Icon: Clock, cls: o.iconAmber, label: 'En Eski Açık Borç',
      value: eskiGun, unit: eskiGun === '—' ? '' : 'gün' },
  ];
  return (
    <div className={o.statGrid}>
      {items.map(({ key, Icon, cls, label, value, unit }) => (
        <div key={key} className={o.statCard}>
          <span className={`${o.statIcon} ${cls}`}><Icon size={18} /></span>
          <span className={o.statBody}>
            <span className={o.statLabel}>{label}</span>
            <span className={o.statValue}>
              {value}
              {unit && <span className={o.statUnit}>{unit}</span>}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

function YaslandirmaKarti({ s }) {
  const dolu = s.buckets.filter((b) => b.amount > 0.005);
  return (
    <section className={d.card} aria-label="Borç yaşlandırma">
      <div className={d.cardHead}>
        <h2 className={d.cardTitle}>Borç Yaşlandırma</h2>
        <span className={d.cardChip}>{tarihTR(s.asOf)} itibarıyla</span>
      </div>

      <div className={d.stack} role="img"
           aria-label={dolu.map((b) => `${b.label} ${formatPercent(b.pct, 1)}`).join(', ') || 'Açık borç yok'}>
        {dolu.map((b) => (
          <span key={b.id}
                style={{ width: `${Math.max(b.pct, 1.5)}%`, background: TONE_RENK[b.tone] }}
                title={`${b.label}: ${formatTRY(b.amount)}`} />
        ))}
      </div>

      <div className={d.bucketList}>
        {s.buckets.map((b) => (
          <div key={b.id} className={d.bucketRow}>
            <span className={d.dot} style={{ background: TONE_RENK[b.tone] }} />
            <span className={d.bucketName}>{b.label}</span>
            <span className={d.bucketDesc}>{TONE_ACIKLAMA[b.tone]}</span>
            <span className={d.bucketPct}>{formatPercent(b.pct, 1)}</span>
            <span className={d.bucketAmt}>{formatTRY(b.amount)}</span>
          </div>
        ))}
      </div>

      {s.future > 0.005 && (
        <p className={d.note}>İleri tarihli borç: {formatTRY(s.future)} (dilimlere dahil değildir)</p>
      )}
      <p className={d.note}>
        Ödemeleriniz en eski borcunuzdan başlayarak düşülür. 30 günü geçen borçlar vadesi geçmiş sayılır.
      </p>
    </section>
  );
}

function TrendTip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const x = payload[0].payload;
  return (
    <div className={d.tip}>
      <div className={d.tipLabel}>{x.label} {x.year}</div>
      <div className={d.tipRow}><span>Borç</span><b>{formatTRY(x.debit)}</b></div>
      <div className={d.tipRow}><span>Ödeme / Alacak</span><b>{formatTRY(x.credit)}</b></div>
      <div className={d.tipRow}><span>Ay sonu bakiye</span><b>{formatTRY(x.balance)}</b></div>
    </div>
  );
}

function TrendKarti({ trend }) {
  return (
    <section className={d.card} aria-label="Aylık hesap hareketi">
      <div className={d.cardHead}>
        <h2 className={d.cardTitle}>Aylık Hesap Hareketi</h2>
        <span className={d.cardChip}>son {trend.length} ay</span>
      </div>
      <div className={d.legend}>
        <span className={d.legendItem}><span className={d.legendSwatch} style={{ background: '#2563C7' }} />Borç</span>
        <span className={d.legendItem}><span className={d.legendSwatch} style={{ background: '#16A36A' }} />Ödeme / Alacak</span>
        <span className={d.legendItem}><span className={d.legendLine} style={{ background: '#EA580C' }} />Ay sonu bakiye</span>
      </div>
      <div className={d.chartBox}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={trend} margin={{ top: 8, right: 6, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false}
                   tick={{ fontSize: 11, fill: '#94A3B8' }} />
            <YAxis tickLine={false} axisLine={false} width={48}
                   tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={formatCompact} />
            <Tooltip content={<TrendTip />} cursor={{ fill: 'rgba(47,128,237,.06)' }} />
            <Bar dataKey="debit" fill="#2563C7" radius={[3, 3, 0, 0]} maxBarSize={16} />
            <Bar dataKey="credit" fill="#16A36A" radius={[3, 3, 0, 0]} maxBarSize={16} />
            <Line dataKey="balance" type="monotone" stroke="#EA580C" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function GunRozeti({ doc }) {
  const ton = DILIM_TONU[doc.bucket] || 'future';
  const metin = doc.days !== null && doc.days >= 0 ? `${formatNumber(doc.days)} gün` : 'İleri tarihli';
  return <span className={d.dayBadge} style={{ color: TONE_RENK[ton] }}>{metin}</span>;
}

function AcikBelgeler({ docs }) {
  const [hepsi, setHepsi] = useState(false);
  const liste = hepsi ? docs.items : docs.items.slice(0, BELGE_ONIZLEME);
  return (
    <section className={d.card} aria-label="Ödenmemiş borç belgeleri">
      <div className={d.cardHead}>
        <h2 className={d.cardTitle}>Ödenmemiş Borç Belgeleri</h2>
        <span className={d.cardChip}>{formatNumber(docs.total)} belge</span>
      </div>
      {docs.items.length === 0 ? (
        <div className={d.okBox}><CheckCircle2 size={18} /> Ödenmemiş borç belgeniz bulunmuyor.</div>
      ) : (
        <>
          <div className={d.tableWrap}>
            <table className={`${d.table} ${d.cardTable} ${d.docTable}`}>
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Belge No</th>
                  <th>İşlem</th>
                  <th className={d.center}>Yaş</th>
                  <th className={d.right}>Belge Tutarı</th>
                  <th className={d.right}>Kalan Borç</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((x) => (
                  <tr key={x.id}>
                    <td className={d.mono}>{tarihTR(x.date)}</td>
                    <td className={d.mono}>{x.docNo || '—'}</td>
                    <td title={x.description}>{x.type}</td>
                    <td className={d.center}><GunRozeti doc={x} /></td>
                    <td className={`${d.right} ${d.mono}`}>{formatTRY(x.amount)}</td>
                    <td className={`${d.right} ${d.mono} ${d.strong}`}>{formatTRY(x.remaining)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {docs.items.length > BELGE_ONIZLEME && (
            <button type="button" className={d.moreBtn} onClick={() => setHepsi((v) => v === false)}>
              {hepsi ? 'Daralt' : `Tümünü göster (${formatNumber(docs.items.length)})`}
            </button>
          )}
          {docs.limited && (
            <p className={d.note}>En eski {formatNumber(docs.items.length)} belge gösteriliyor.</p>
          )}
        </>
      )}
    </section>
  );
}

function Pager({ cur, totalPages, totalItems, per, onPage, onPer }) {
  let list;
  if (totalPages <= 7) list = Array.from({ length: totalPages }, (_, i) => i + 1);
  else if (cur <= 4) list = [1, 2, 3, 4, 5, '…', totalPages];
  else if (cur >= totalPages - 3) {
    list = [1, '…', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  } else list = [1, '…', cur - 1, cur, cur + 1, '…', totalPages];

  return (
    <div className={o.pager}>
      <span className={o.pagerInfo}>
        <b>{formatNumber(totalItems)}</b> hareket · Sayfa {cur}/{totalPages}
      </span>
      <label className={o.perLabel}>
        Sayfa başına
        <select className={o.select} value={per} onChange={(e) => onPer(Number(e.target.value))}>
          {PER_PAGE.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      {totalPages > 1 && (
        <div className={o.pages}>
          <button type="button" className={o.pageBtn} disabled={cur <= 1}
                  onClick={() => onPage(cur - 1)} aria-label="Önceki sayfa">
            <ChevronLeft size={15} />
          </button>
          {list.map((n, i) => (n === '…' ? (
            <span key={`d${i}`} className={o.dots}>…</span>
          ) : (
            <button key={n} type="button"
                    className={`${o.pageBtn} ${n === cur ? o.pageOn : ''}`}
                    aria-current={n === cur ? 'page' : undefined}
                    onClick={() => onPage(n)}>
              {n}
            </button>
          )))}
          <button type="button" className={o.pageBtn} disabled={cur >= totalPages}
                  onClick={() => onPage(cur + 1)} aria-label="Sonraki sayfa">
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

function Hareketler({ f, h, onChange, onClear, onPage }) {
  const filtreSayisi = [f.q, f.from, f.to, f.type].filter(Boolean).length;
  return (
    <>
      <div className={o.filterBar}>
        <span className={o.searchBox}>
          <span className={o.searchIcon}><Search size={14} /></span>
          <input className={o.searchInput} type="text" value={f.q}
                 onChange={(e) => onChange({ q: e.target.value })}
                 placeholder="Belge no veya açıklama..." aria-label="Hareketlerde ara" />
          {f.q && (
            <button type="button" className={o.searchClear}
                    onClick={() => onChange({ q: '' })} aria-label="Aramayı temizle">
              <X size={12} />
            </button>
          )}
        </span>

        <span className={o.filterField}>
          <span className={o.filterLabel}><Calendar size={11} /> Tarih</span>
          <input type="date" className={o.dateInput} value={f.from} max={f.to || undefined}
                 aria-label="Başlangıç tarihi" onChange={(e) => onChange({ from: e.target.value })} />
          <span className={o.filterLabel}>–</span>
          <input type="date" className={o.dateInput} value={f.to} min={f.from || undefined}
                 aria-label="Bitiş tarihi" onChange={(e) => onChange({ to: e.target.value })} />
        </span>

        <span className={o.filterField}>
          <span className={o.filterLabel}><Filter size={11} /> Tür</span>
          <select className={`${o.select} ${f.type ? o.selectOn : ''}`} value={f.type}
                  onChange={(e) => onChange({ type: e.target.value })} aria-label="Hareket türü">
            {TIPLER.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </span>

        <span className={o.barSpacer} />
        <span className={o.resultCount}><b>{formatNumber(h.total)}</b> hareket</span>
        {filtreSayisi > 0 && (
          <button type="button" className={o.clearBtn} onClick={onClear}>
            <X size={13} /> Temizle ({filtreSayisi})
          </button>
        )}
      </div>

      {h.error && (
        <div className={o.stateBar} role="alert">
          <span>{h.error}</span>
          <button type="button" className={o.retryBtn} onClick={h.yenile}>
            <RotateCw size={12} /> Tekrar dene
          </button>
        </div>
      )}

      <section className={`${d.card} ${h.refreshing ? o.fading : ''}`} aria-label="Hesap hareketleri">
        <div className={d.statsLine}>
          <span>Borç <b className={d.debit}>{formatTRY(h.stats.debit)}</b></span>
          <span>Alacak <b className={d.credit}>{formatTRY(h.stats.credit)}</b></span>
          <span>Fark <b>{formatTRY(h.stats.net)}</b></span>
          {filtreSayisi > 0 && <span className={d.muted}>(filtrelenen hareketler)</span>}
        </div>

        {h.loading ? (
          <div className={o.skel} />
        ) : h.items.length === 0 ? (
          <div className={o.empty}>
            <span className={o.emptyIcon}><Inbox size={34} strokeWidth={1.3} /></span>
            <div>{filtreSayisi > 0 ? 'Bu filtrelere uygun hareket bulunamadı.' : 'Hesap hareketiniz bulunmuyor.'}</div>
          </div>
        ) : (
          <div className={d.tableWrap}>
            <table className={`${d.table} ${d.cardTable} ${d.moveTable}`}>
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Belge No</th>
                  <th>İşlem</th>
                  <th className={d.right}>Borç</th>
                  <th className={d.right}>Alacak</th>
                  <th className={d.right}>Bakiye</th>
                </tr>
              </thead>
              <tbody>
                {h.items.map((x) => {
                  const borcMu = x.debit > 0;
                  return (
                    <tr key={x.id}>
                      <td className={d.mono}>{tarihTR(x.date)}</td>
                      <td className={d.mono}>{x.docNo || '—'}</td>
                      <td title={x.description}>
                        <span className={`${d.kindIcon} ${borcMu ? d.kindDebit : d.kindCredit}`}>
                          {borcMu ? <ArrowUpRight size={13} /> : <ArrowDownLeft size={13} />}
                        </span>
                        {x.type}
                      </td>
                      <td className={`${d.right} ${d.mono} ${d.debit}`}>{x.debit > 0 ? formatTRY(x.debit) : '—'}</td>
                      <td className={`${d.right} ${d.mono} ${d.credit}`}>{x.credit > 0 ? formatTRY(x.credit) : '—'}</td>
                      <td className={`${d.right} ${d.mono}`}>{formatTRY(x.balance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {h.loading === false && h.total > 0 && (
        <Pager cur={h.page} totalPages={h.totalPages} totalItems={h.total} per={f.per}
               onPage={onPage} onPer={(n) => onChange({ per: n })} />
      )}
    </>
  );
}

// ---------- sayfa ----------
export default function DebtV2() {
  const [sp, setSp] = useSearchParams();
  const [f, setF] = useState(() => urldenOku(sp));
  const [headerTerm, setHeaderTerm] = useState('');

  const cart = useCartContext();
  const toast = useToast();
  const kurlar = useExchangeRates();
  const handleMenu = useMenuNav(toast.push);
  const ozet = useDebtSummary();
  const hareket = useDebtTransactions(f, f.tab === 'moves');
  const s = ozet.data;

  useEffect(() => {
    const yeni = urleYaz(f);
    if (yeni !== sp.toString()) setSp(new URLSearchParams(yeni), { replace: true });
  }, [f, sp, setSp]);

  const guncelle = useCallback((yama) => {
    setF((x) => duzelt({ ...x, ...yama, page: 'page' in yama ? yama.page : 1 }));
  }, []);

  const sayfaya = useCallback((n) => {
    guncelle({ page: n });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [guncelle]);

  const temizle = useCallback(() => setF((x) => duzelt({ tab: 'moves', per: x.per })), []);
  const risk = s ? (RISK[s.risk] || RISK.clean) : null;

  return (
    <div className={styles.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <Header
        searchTerm={headerTerm}
        onSearchChange={setHeaderTerm}
        cart={cart}
        eurRate={kurlar.eurRate}
        onMenuSelect={handleMenu}
      />

      <main className={styles.main}>
        <div className={o.head}>
          <h1 className={o.title}>
            Borç Yaşlandırma
            <span className={o.titleSub}>Cari hesap durumunuz ve vadesi geçmiş borçlarınız</span>
          </h1>
          {s && (
            <span className={o.headChips}>
              <span className={`${d.riskBadge} ${d[risk.cls]}`}>{risk.label}</span>
              <OdemeLinki url={s.paymentUrl} className={d.payLink}>
                <CreditCard size={14} /> Ödeme Yap
              </OdemeLinki>
            </span>
          )}
        </div>

        {ozet.error && (
          <div className={o.stateBar} role="alert">
            <span>{ozet.error}</span>
            <button type="button" className={o.retryBtn} onClick={ozet.yenile}>
              <RotateCw size={12} /> Tekrar dene
            </button>
          </div>
        )}

        {s && <KisitlamaBandi r={s.restriction} url={s.paymentUrl} />}

        <div className={d.tabs} role="tablist" aria-label="Borç sayfası">
          <button type="button" role="tab" aria-selected={f.tab === 'overview'}
                  className={`${d.tab} ${f.tab === 'overview' ? d.tabOn : ''}`}
                  onClick={() => guncelle({ tab: 'overview' })}>
            <Wallet size={15} /> Özet
          </button>
          <button type="button" role="tab" aria-selected={f.tab === 'moves'}
                  className={`${d.tab} ${f.tab === 'moves' ? d.tabOn : ''}`}
                  onClick={() => guncelle({ tab: 'moves' })}>
            <History size={15} /> Hesap Hareketleri
            {s && <span className={d.tabCount}>{formatNumber(s.totals.count)}</span>}
          </button>
        </div>

        {f.tab === 'moves' ? (
          <Hareketler f={f} h={hareket} onChange={guncelle} onClear={temizle} onPage={sayfaya} />
        ) : s === null ? (
          ozet.loading && (
            <div className={o.list}>
              {[0, 1, 2].map((i) => <div key={i} className={o.skel} />)}
            </div>
          )
        ) : (
          <>
            <StatCards s={s} />
            {s.overdue <= 0.005 && s.totals.count > 0 && (
              <div className={`${d.okBox} ${d.okTop}`}>
                <CheckCircle2 size={18} /> Vadesi geçmiş borcunuz bulunmuyor.
              </div>
            )}
            <div className={d.grid2}>
              <YaslandirmaKarti s={s} />
              <TrendKarti trend={s.trend} />
            </div>
            <AcikBelgeler docs={s.openDocs} />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
